import { useState, useMemo } from 'react';
import {
  TrendingUp, DollarSign, CheckCircle, Clock, Briefcase,
  ChevronRight, AlertCircle
} from 'lucide-react';
import { useCreatorPortal } from '../../contexts/CreatorPortalContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useCountUp } from '../../hooks/useCountUp';
import { formatEuro } from '../../utils/currency';
import type { CreatorCampaignData } from '../../services/creatorPortalService';

type TimePeriod = 'month' | 'ytd' | 'year';

interface KpiCardProps {
  label: string;
  numericValue: number;
  subtitle: string;
  icon: React.ElementType;
  accentColor: string;
  borderColor: string;
  iconBg: string;
  expandedContent?: React.ReactNode;
  isCurrency?: boolean;
  badge?: { label: string; color: string };
}

function KpiCard({
  label, subtitle, icon: Icon,
  accentColor, borderColor, iconBg, expandedContent, numericValue, isCurrency, badge,
}: KpiCardProps) {
  const [expanded, setExpanded] = useState(false);
  const animatedValue = useCountUp({ end: numericValue, duration: 1200, decimals: isCurrency ? 2 : 0 });
  const displayValue = isCurrency ? formatEuro(animatedValue) : String(Math.floor(animatedValue));

  return (
    <div className={`bg-white rounded-card shadow-card border-l-4 ${borderColor} overflow-hidden transition-all duration-200 hover:shadow-card-hover hover:scale-[1.01]`}>
      <button
        onClick={() => expandedContent && setExpanded(!expanded)}
        aria-expanded={expandedContent ? expanded : undefined}
        className="w-full text-left p-5 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand-ink/20"
      >
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</p>
              {badge && (
                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-xl ${badge.color}`}>
                  {badge.label}
                </span>
              )}
            </div>
            <p className={`text-2xl font-bold ${accentColor} tabular-nums`}>{displayValue}</p>
            <p className="text-xs text-gray-500 mt-1 truncate">{subtitle}</p>
            {expandedContent && (
              <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-gray-600">
                <ChevronRight className={`w-3.5 h-3.5 transition-transform duration-150 ${expanded ? 'rotate-90' : ''}`} />
                {expanded ? 'Fechar detalhes' : 'Ver detalhes'}
              </span>
            )}
          </div>
          <div className="flex flex-col items-center gap-1">
            <div className={`w-10 h-10 ${iconBg} rounded-xl flex items-center justify-center`}>
              <Icon className={`w-5 h-5 ${accentColor}`} />
            </div>
          </div>
        </div>
      </button>
      {expanded && expandedContent && (
        <div className="px-5 pb-5 border-t border-gray-100 pt-3 bg-gray-50 animate-in fade-in slide-in-from-top-1 duration-200">
          {expandedContent}
        </div>
      )}
    </div>
  );
}

function CampaignListExpanded({
  items,
  emptyMessage,
  setSelectedCampaignId,
  valueColor = 'text-gray-900',
}: {
  items: { id: string; name: string; value: number }[];
  emptyMessage: string;
  setSelectedCampaignId: (id: string | null) => void;
  valueColor?: string;
}) {
  if (items.length === 0) {
    return <p className="text-sm text-gray-500">{emptyMessage}</p>;
  }
  return (
    <div className="space-y-1.5 max-h-44 overflow-y-auto">
      {items.map(item => (
        <button
          key={item.id}
          onClick={() => setSelectedCampaignId(item.id)}
          className="w-full flex items-center justify-between text-sm hover:bg-gray-50 rounded-lg p-1.5 -mx-1.5 transition-colors"
        >
          <span className="text-gray-700 truncate">{item.name}</span>
          <span className={`font-medium whitespace-nowrap ml-2 ${valueColor}`}>
            {formatEuro(item.value)}
          </span>
        </button>
      ))}
    </div>
  );
}

function PendingReceivablesCard({ campaigns, setSelectedCampaignId }: { campaigns: CreatorCampaignData[]; setSelectedCampaignId: (id: string | null) => void }) {
  const { t } = useLanguage();

  const { ready, outstanding, pending, grandTotal } = useMemo(() => {
    const readyCampaigns: { campaign: CreatorCampaignData; amount: number }[] = [];
    const outstandingCampaigns: { campaign: CreatorCampaignData; amount: number }[] = [];
    const pendingCampaigns: { campaign: CreatorCampaignData; amount: number }[] = [];

    for (const c of campaigns) {
      if (c.derived_influencer_invoice_status === 'Paid') continue;
      const actualPaid = c.influencer_amount_paid;
      const netOwed = c.net_earnings - actualPaid;
      if (netOwed <= 0) continue;

      if (c.amount_received > 0) {
        const readyAmount = c.net_payable;
        if (readyAmount > 0) {
          readyCampaigns.push({ campaign: c, amount: readyAmount });
        }
        const remainder = netOwed - Math.max(readyAmount, 0);
        if (remainder > 0) {
          if (c.company_invoice_status === 'Issued' || c.company_invoice_status === 'Overdue') {
            outstandingCampaigns.push({ campaign: c, amount: remainder });
          } else if (c.company_invoice_status === 'Not Issued') {
            pendingCampaigns.push({ campaign: c, amount: remainder });
          }
        }
      } else if (c.company_invoice_status === 'Issued' || c.company_invoice_status === 'Overdue') {
        outstandingCampaigns.push({ campaign: c, amount: netOwed });
      } else {
        const s = c.status.toLowerCase();
        if (s !== 'consultation' && s !== 'consulta') {
          pendingCampaigns.push({ campaign: c, amount: netOwed });
        }
      }
    }

    const readyTotal = readyCampaigns.reduce((s, r) => s + r.amount, 0);
    const outstandingTotal = outstandingCampaigns.reduce((s, r) => s + r.amount, 0);
    const pendingTotal = pendingCampaigns.reduce((s, r) => s + r.amount, 0);

    return {
      ready: { campaigns: readyCampaigns, total: readyTotal },
      outstanding: { campaigns: outstandingCampaigns, total: outstandingTotal },
      pending: { campaigns: pendingCampaigns, total: pendingTotal },
      grandTotal: readyTotal + outstandingTotal + pendingTotal,
    };
  }, [campaigns]);

  const allCount = ready.campaigns.length + outstanding.campaigns.length + pending.campaigns.length;

  return (
    <KpiCard
      label={t('creatorsHub.receivables')}
      numericValue={grandTotal}
      isCurrency
      subtitle={`${allCount} ${t('creatorsHub.receivables.subtitle')}`}
      icon={AlertCircle}
      accentColor="text-orange-600"
      borderColor="border-l-orange-500"
      iconBg="bg-orange-50"
      expandedContent={
        grandTotal > 0 ? (
          <div className="space-y-3">
            {ready.total > 0 && (
              <ReceivableSection
                label={t('creatorsHub.receivables.ready')}
                desc={t('creatorsHub.receivables.readyDesc')}
                total={ready.total}
                items={ready.campaigns}
                color="text-emerald-600"
                dotColor="bg-emerald-500"
                setSelectedCampaignId={setSelectedCampaignId}
              />
            )}
            {outstanding.total > 0 && (
              <ReceivableSection
                label={t('creatorsHub.receivables.outstanding')}
                desc={t('creatorsHub.receivables.outstandingDesc')}
                total={outstanding.total}
                items={outstanding.campaigns}
                color="text-amber-600"
                dotColor="bg-amber-500"
                setSelectedCampaignId={setSelectedCampaignId}
              />
            )}
            {pending.total > 0 && (
              <ReceivableSection
                label={t('creatorsHub.receivables.pending')}
                desc={t('creatorsHub.receivables.pendingDesc')}
                total={pending.total}
                items={pending.campaigns}
                color="text-gray-500"
                dotColor="bg-gray-400"
                setSelectedCampaignId={setSelectedCampaignId}
              />
            )}
            <div className="border-t border-gray-200 pt-2 flex justify-between items-center">
              <span className="text-sm font-semibold text-gray-900">{t('creatorsHub.receivables.grandTotal')}</span>
              <span className="text-sm font-bold text-orange-600">{formatEuro(grandTotal)}</span>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-500">{t('creatorsHub.receivables.noReceivables')}</p>
        )
      }
    />
  );
}

function ReceivableSection({
  label, desc, total, items, color, dotColor, setSelectedCampaignId,
}: {
  label: string;
  desc: string;
  total: number;
  items: { campaign: CreatorCampaignData; amount: number }[];
  color: string;
  dotColor: string;
  setSelectedCampaignId: (id: string | null) => void;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1.5">
          <div className={`w-2 h-2 rounded-full ${dotColor}`} />
          <span className="text-xs font-semibold text-gray-700">{label}</span>
        </div>
        <span className={`text-xs font-bold ${color}`}>{formatEuro(total)}</span>
      </div>
      <p className="text-[10px] text-gray-400 mb-1.5 ml-3.5">{desc}</p>
      <div className="space-y-1 max-h-24 overflow-y-auto ml-3.5">
        {items.map(({ campaign, amount }) => (
          <button
            key={campaign.id}
            onClick={() => setSelectedCampaignId(campaign.id)}
            className="w-full flex items-center justify-between text-xs hover:bg-gray-50 rounded p-1 -mx-1 transition-colors"
          >
            <span className="text-gray-600 truncate">{campaign.name}</span>
            <span className={`font-medium whitespace-nowrap ml-2 ${color}`}>{formatEuro(amount)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function PeriodSelector({ value, onChange }: { value: TimePeriod; onChange: (p: TimePeriod) => void }) {
  const { t } = useLanguage();
  const options: { key: TimePeriod; label: string }[] = [
    { key: 'month', label: t('creatorsHub.kpi.thisMonth') },
    { key: 'ytd', label: t('creatorsHub.kpi.ytd') },
    { key: 'year', label: t('creatorsHub.kpi.fullYear') },
  ];

  return (
    <div className="flex items-center gap-0.5 bg-gray-100 rounded-lg p-0.5">
      {options.map(opt => (
        <button
          key={opt.key}
          onClick={() => onChange(opt.key)}
          className={`text-[11px] font-medium px-3 py-1.5 rounded-xl transition-all duration-200 ${
            value === opt.key
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function getRevenueDate(c: CreatorCampaignData): Date | null {
  const d = c.company_payment_received_date || c.last_payment_date;
  if (!d) return null;
  return new Date(d + 'T00:00:00');
}

export function CreatorsHubKPIs() {
  const { t } = useLanguage();
  const {
    campaigns, activeCampaigns, completedCampaigns, consultationCampaigns,
    awaitingClientPaymentCampaigns, pipelineCampaigns, setSelectedCampaignId,
  } = useCreatorPortal();

  const [period, setPeriod] = useState<TimePeriod>('ytd');

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  const revenueData = useMemo(() => {
    const paidCampaigns = campaigns.filter(c => c.influencer_amount_paid > 0);

    const filterByPeriod = (list: CreatorCampaignData[]) => {
      return list.filter(c => {
        const date = getRevenueDate(c);
        if (!date) {
          return c.year === currentYear;
        }
        if (period === 'month') {
          return date.getFullYear() === currentYear && date.getMonth() === currentMonth;
        }
        if (period === 'ytd') {
          return date.getFullYear() === currentYear && date.getMonth() <= currentMonth;
        }
        return date.getFullYear() === currentYear;
      });
    };

    const filtered = filterByPeriod(paidCampaigns);
    const totalNet = filtered.reduce((sum, c) => sum + c.influencer_amount_paid, 0);
    const count = filtered.length;

    return { filtered, totalNet, count };
  }, [campaigns, period, currentYear, currentMonth]);

  const readyForPayment = useMemo(() => {
    return campaigns
      .filter(c => {
        if (c.derived_influencer_invoice_status === 'Paid') return false;
        return c.net_payable > 0 && c.amount_received > 0;
      })
      .map(c => ({ id: c.id, name: c.name, value: c.net_payable }));
  }, [campaigns]);
  const readyForPaymentAmount = readyForPayment.reduce((sum, r) => sum + r.value, 0);

  const awaitingClientData = useMemo(() => {
    return awaitingClientPaymentCampaigns.map(c => ({
      id: c.id,
      name: c.name,
      value: c.net_earnings,
    }));
  }, [awaitingClientPaymentCampaigns]);
  const awaitingClientAmount = awaitingClientData.reduce((sum, r) => sum + r.value, 0);

  const pipelineData = useMemo(() => {
    return pipelineCampaigns
      .filter(c => {
        const s = c.status.toLowerCase();
        return s !== 'consultation' && s !== 'consulta';
      })
      .map(c => ({ id: c.id, name: c.name, value: c.net_earnings }));
  }, [pipelineCampaigns]);
  const pipelineAmount = pipelineData.reduce((sum, r) => sum + r.value, 0);

  const fullyPaidTotal = useMemo(() => {
    return completedCampaigns.reduce((sum, c) => sum + c.influencer_amount_paid, 0);
  }, [completedCampaigns]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
          {t('creatorsHub.kpi.overview')}
        </h2>
        <PeriodSelector value={period} onChange={setPeriod} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {/* 1. My Revenue */}
        <KpiCard
          label={t('creatorsHub.kpi.myRevenue')}
          numericValue={revenueData.totalNet}
          isCurrency
          subtitle={t('creatorsHub.campaignsCount').replace('{0}', String(revenueData.count))}
          icon={TrendingUp}
          accentColor="text-blue-600"
          borderColor="border-l-blue-500"
          iconBg="bg-blue-50"
          badge={{
            label: period === 'month' ? t('creatorsHub.kpi.thisMonth') : period === 'ytd' ? t('creatorsHub.kpi.ytd') : String(currentYear),
            color: 'bg-blue-100 text-blue-700',
          }}
          expandedContent={
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">{t('creatorsHub.active')}</span>
                <span className="font-medium text-gray-900">
                  {formatEuro(activeCampaigns.reduce((s, c) => s + c.influencer_amount_paid, 0))}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">{t('creatorsHub.completed')}</span>
                <span className="font-medium text-gray-900">
                  {formatEuro(completedCampaigns.reduce((s, c) => s + c.influencer_amount_paid, 0))}
                </span>
              </div>
              <div className="border-t border-gray-100 pt-2 flex justify-between text-xs text-gray-400">
                <span>{t('creatorsHub.kpi.totalAgreed')}</span>
                <span>{formatEuro(campaigns.reduce((s, c) => s + c.net_earnings, 0))}</span>
              </div>
            </div>
          }
        />

        {/* 2. Ready for Payment */}
        <KpiCard
          label={t('creatorsHub.readyForPayment')}
          numericValue={readyForPaymentAmount}
          isCurrency
          subtitle={t('creatorsHub.kpi.readySubtitle').replace('{0}', String(readyForPayment.length))}
          icon={DollarSign}
          accentColor="text-emerald-600"
          borderColor="border-l-emerald-500"
          iconBg="bg-emerald-50"
          expandedContent={
            <CampaignListExpanded
              items={readyForPayment}
              emptyMessage={t('creatorsHub.noReadyPayments')}
              setSelectedCampaignId={setSelectedCampaignId}
              valueColor="text-emerald-600"
            />
          }
        />

        {/* 3. Pending Receivables */}
        <PendingReceivablesCard campaigns={campaigns} setSelectedCampaignId={setSelectedCampaignId} />

        {/* 4. Awaiting Client Payment */}
        <KpiCard
          label={t('creatorsHub.kpi.awaitingClient')}
          numericValue={awaitingClientAmount}
          isCurrency
          subtitle={t('creatorsHub.kpi.awaitingClientSubtitle').replace('{0}', String(awaitingClientData.length))}
          icon={Clock}
          accentColor="text-amber-600"
          borderColor="border-l-amber-500"
          iconBg="bg-amber-50"
          expandedContent={
            <CampaignListExpanded
              items={awaitingClientData}
              emptyMessage={t('creatorsHub.kpi.noAwaitingClient')}
              setSelectedCampaignId={setSelectedCampaignId}
              valueColor="text-amber-600"
            />
          }
        />

        {/* 5. Pipeline */}
        <KpiCard
          label={t('creatorsHub.kpi.pipeline')}
          numericValue={pipelineAmount}
          isCurrency
          subtitle={`${pipelineData.length} ${t('creatorsHub.inPipeline')}`}
          icon={Briefcase}
          accentColor="text-gray-600"
          borderColor="border-l-gray-400"
          iconBg="bg-gray-100"
          expandedContent={
            <div className="space-y-3">
              {pipelineData.length > 0 ? (
                <CampaignListExpanded
                  items={pipelineData}
                  emptyMessage=""
                  setSelectedCampaignId={setSelectedCampaignId}
                  valueColor="text-gray-600"
                />
              ) : (
                <p className="text-sm text-gray-500">{t('creatorsHub.noPipeline')}</p>
              )}
              {consultationCampaigns.length > 0 && (
                <div className="border-t border-gray-100 pt-2">
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1.5">{t('creatorsHub.consultation')}</p>
                  <div className="space-y-1 max-h-24 overflow-y-auto">
                    {consultationCampaigns.map(c => (
                      <button
                        key={c.id}
                        onClick={() => setSelectedCampaignId(c.id)}
                        className="w-full flex items-center justify-between text-xs hover:bg-gray-50 rounded p-1 transition-colors"
                      >
                        <span className="text-gray-500 truncate">{c.name}</span>
                        <span className="font-medium text-gray-400 whitespace-nowrap ml-2">{formatEuro(c.net_earnings)}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          }
        />

        {/* 6. Fully Paid */}
        <KpiCard
          label={t('creatorsHub.fullyPaid')}
          numericValue={fullyPaidTotal}
          isCurrency
          subtitle={`${completedCampaigns.length} ${t('creatorsHub.completedPaid')}`}
          icon={CheckCircle}
          accentColor="text-green-600"
          borderColor="border-l-green-500"
          iconBg="bg-green-50"
          expandedContent={
            completedCampaigns.length > 0 ? (
              <CampaignListExpanded
                items={completedCampaigns.map(c => ({ id: c.id, name: c.name, value: c.influencer_amount_paid }))}
                emptyMessage=""
                setSelectedCampaignId={setSelectedCampaignId}
                valueColor="text-green-600"
              />
            ) : undefined
          }
        />
      </div>
    </div>
  );
}
